import _ from 'lodash';

import createModel from './lib/create_model';

const SOURCES = ['roleFeatures', 'gatekeeperFeatures', 'browserCapabilities'];
// TODO: remove ignore sources loaded once we get proper blocking on loading the app features for all sources.
const IGNORE_SOURCES_LOADED = ['demoMode'];

export const FeatureList = createModel({
  modelName: 'FeatureList',

  properties: {
    /* FEATURE FLAGS */

    // Gates the sagan-admin Browsing Engagement admin page for configuring
    // per-org Beacon rate limits
    browsingEngagement: Boolean,

    // Enables new Chat V2 enablement option in Admin experience
    chatV2: Boolean,

    // Enables rich text formatting (bold/italic/underline/links) in the chat composer
    chatRichTextFormatting: Boolean,

    // Enables "Contact Support" menu link for the users with correct roles
    contactSupport: Boolean,

    // enables the Conversational Analytics chat panel in Reports
    conversationalAnalytics: Boolean,

    // load the cookieless versions of the Looker embeds
    cookielessLookerEmbed: Boolean,

    // enables dark mode theme toggle in the top bar
    darkMode: Boolean,

    debug: Boolean,

    // enable dedicated hero capabilities
    dedicatedHero: Boolean,

    demoMode: Boolean,

    // can accept/make phones calls over direct dial
    directDial: Boolean,

    // disable analytics tracking (e.g. segment), typically used for non-production orgs
    disableAnalytics: Boolean,

    // disable Keyword Groups in admin part
    disableKeywordGroup: Boolean,

    // suppress new version notifications
    disableNewVersionNotifications: Boolean,

    // show a global yellow banner for overdue payment messaging
    paymentOverdueBanner: Boolean,

    // enables inline PDF preview in the attachment modal
    pdfPreview: Boolean,

    // overwrites multi-source-customer-lookup display behavior to use customer-profile-def to display externalCustomerId
    displayLegacyExternalId: Boolean,

    doubleInboxPollingInterval1: Boolean,
    doubleInboxPollingInterval2: Boolean,

    // Enable Edit and Add HTTP2 Node in IVR Editor
    enableEditingHttp2Node: Boolean,

    // Enable adding the SIP Refer node in IVR Editor
    enableAddingSipReferNode: Boolean,

    // use HTML to generate plain text
    emailPlainTextFromHtml: Boolean,

    // Enable HeroAI tools in the new Email composer
    enableEmailComposerHeroAI: Boolean,

    // enable Gemini conversational analytics in Looker embeds
    geminiInLooker: Boolean,

    // enable outgoing chat over http
    enableOutgoingChatHttp: Boolean,

    // enable "fake webRTC" channel to prevent browser throttling
    enableFakeWebRTC: Boolean,

    // forces the email composer to be the new slate composer
    forceEmailSlateBeta: Boolean,

    // makes the side panel extend to full page height, moving the footer under the conversation feed only
    fullHeightSidePanel: Boolean,

    // enables variable interpolation in URL links in the answer editor
    interpolateVariablesInURL: Boolean,

    // allows user creation  on first login attempt using SSO
    allowJitUserCreation: Boolean,

    // new property 'loop the queue and hold music' on the voice editor page
    loopHoldMusic: Boolean,

    // mask the routing counts in the agent status button if the user does not have liveboards permission
    maskRoutingCounts: Boolean,

    // whether the browser supports and the user allowed the microphone
    microphone: Boolean,

    // Activate UI to allow for configuration of SMS endpoints to be controlled by message automation.
    smsMessageAutomation: Boolean,

    // edit Chat configuration for mobile
    sidekickMobileSDK: Boolean,

    // Gates Sidekick for Sales chat-related UI work
    sidekickSalesChatV2: Boolean,

    // can accept/make phone calls over SIP
    sip: Boolean,

    // new version of slate
    slateBeta: Boolean,

    // can send/receive sms messages
    sms: Boolean,

    // enable warning on email conversation items when spam is detected
    spamEmailWarning: Boolean,

    // enable spam rule condition
    spamRuleCondition: Boolean,

    // enables UI controls that trigger AI Suggested Replies features
    suggestedReplies: Boolean,

    // enables CTI integration with third party telephony systems
    telephonyApi: Boolean,

    // enables team-level messaging capacity configuration
    teamMessagingCapacity: Boolean,

    // enables the Team Assist AI copilot panel
    teamAssistAI: Boolean,

    // testFlag is used only for testing, as feature flags should generally be short-lived
    testFlag: Boolean,

    // Enable endpoint cache in telephony service
    useEndpointCache: Boolean,

    // enables user survey on away page
    userSurvey: Boolean,

    // can accept/make phones calls over webRTC
    webRTC: Boolean,

    // adjust keepalive time for MQTT client
    doubleKeepAlive1: Boolean,
    doubleKeepAlive2: Boolean,
    tripleKeepAlive: Boolean,

    // adjust reconnect period for MQTT client
    doubleReconnectPeriod: Boolean,
    tripleReconnectPeriod: Boolean,

    /* ROLE FEATURES */

    // - can navigate to admin settings
    adminSettings: Boolean,

    // allows to update agent status
    agentStatusControl: Boolean,

    // can create and update campaigns
    campaignsManagement: Boolean,

    // can create and update channels
    channelManagement: Boolean,

    // can create & update custom attributes
    customAttributeManagement: Boolean,

    // can edit assignee (dedicated hero) for customer
    'customer:dedicatedHero': Boolean,

    // can create and update email domains
    emailDomainsManagement: Boolean,

    // can delete communication attachments
    manageAttachments: Boolean,

    // can download and delete phone call and voicemail recordings
    manageVoiceRecordings: Boolean,

    // can view and control Mobile Phone Number Identification
    mobileNumberLookupManagement: Boolean,

    // can view all group inboxes (in the dashboard)
    'inboxes:viewAll': Boolean,

    // - can create and edit routing groups
    // - can assign agents to routing groups
    inboxManagement: Boolean,

    // - can list, add, delete personal API tokens
    // - can list, add, delete webhooks
    devManagement: Boolean,

    // can view developer debugging tools
    debuggingView: Boolean,

    // - can delete profile
    // - can redact conversation item
    complianceManagement: Boolean,

    // can view and edit integrations (eg. customer lookup)
    integrationsManagement: Boolean,

    // can view and edit IVR configuration
    ivrConfigurationManagement: Boolean,

    // can listen to live calls
    'conversation:callCoach': Boolean,

    // can listen to call recordings
    'conversation:callRecordingListen': Boolean,

    // can manage conversations (eg. profiles, tasks, notes, search)
    // - can be routed conversations
    // - can work on conversations (task, notes, etc.)
    // - can search across all conversations
    'conversation:general': Boolean,

    // can send outbound communications
    'conversation:outbound': Boolean,

    // can view conversations
    'conversation:read': Boolean,

    // can moderate social comments
    'conversation:socialCommentModeration': Boolean,

    // can edit answers/variables
    'answers:admin': Boolean,

    // can read answers
    'answers:read': Boolean,

    // standalone login permission
    login: Boolean,

    // can build reports
    reportBuilder: Boolean,

    // can manage roles that can be assigned to users
    roleManagement: Boolean,

    // can edit rules
    rulesManagement: Boolean,

    // add support for flexible cards in the admin
    adminFlexibleCardEnabled: Boolean,

    // enable access to sidekick conversation UI
    sidekickConversationManagement: Boolean,

    // can edit skills
    skillManagement: Boolean,

    // whether the user has logged in using SSO
    sso: Boolean,

    // can edit single sign-on configuration
    ssoConfigurationManagement: Boolean,

    // can view the existing users
    // can edit teams
    teamManagement: Boolean,

    // can create & update topics
    'topics:admin': Boolean,

    // userManagement enables the following:
    // - can create and edit users (agents only)
    // - can trigger password reset for users (agents only)
    // - can edit agent channels
    userManagement: Boolean,

    // - can navigate to answer performance page
    viewAnswerPerformance: Boolean,

    // - can navigate to guide performance page
    viewGuidePerformance: Boolean,

    // can view liveboards
    viewLiveboards: Boolean,

    // can view reports
    'reports:read': Boolean,

    // can view webhooks admin page
    webhooks: Boolean,

    // in-app notification For Answer Threads Classroom
    atClassroomNotification: Boolean,

    // receives in-app notifications for Sidekick Customers
    sidekickNotifications: Boolean,

    // use topic IDs selector in Sidekick Threads in AddTopics action if possible
    sidekickThreadUseTopicIds: Boolean,

    // can view the App Actions UI
    showLasagnaPage: Boolean,
  },
});

const AppFeatures = createModel({
  modelName: 'AppFeatures',

  properties: {
    roleFeatures: FeatureList,
    gatekeeperFeatures: FeatureList,
    browserCapabilities: FeatureList,
  },

  replace(source, features) {
    const featureList = new FeatureList(features);
    const newAttrs = _.merge({}, this, { [source]: featureList });
    return new AppFeatures(newAttrs);
  },

  sourcesAreLoaded() {
    return _.chain(this).pick(SOURCES).values().every().value();
  },

  isEnabled(feature) {
    if (this.sourcesAreLoaded() || _.includes(IGNORE_SOURCES_LOADED, feature)) {
      return (
        _.some(SOURCES, source => this[source] && this[source][feature] === true) &&
        _.every(SOURCES, source => this[source] && this[source][feature] !== false)
      );
    }

    return false;
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },
    SOURCES,
  },
});

export default AppFeatures;
